import { useMemo } from 'react';
import { useProject } from '@/hooks/useProjects';
import { useTasks } from '@/hooks/useTasks';
import {
    calculateProjectCompletionDate,
    calculateRiskStatus,
    calculateDaysDifference,
    type RiskStatus,
} from '@/utils/completionDateCalculation';
import { parseLocalDate } from '@/utils/dates';

export interface ProjectRiskInfo {
    estimatedCompletion: Date | null;
    committedDate: Date | null;
    riskStatus: RiskStatus;
    daysDifference: number;
    isAtRisk: boolean;
}

/**
 * Derives real-time risk info for a project from its current tasks.
 * Uses the tasks' end_dates to compute estimated completion client-side,
 * so the UI reflects task changes immediately (before the DB trigger fires).
 */
export const useProjectRisk = (projectId: number): ProjectRiskInfo | null => {
    const { data: project } = useProject(projectId);
    const { data: tasks } = useTasks(projectId);

    return useMemo(() => {
        if (!project || !tasks) return null;

        const parsedCommitted = project.committed_delivery_date
            ? parseLocalDate(project.committed_delivery_date)
            : null;
        const committedDate =
            parsedCommitted && !isNaN(parsedCommitted.getTime()) ? parsedCommitted : null;

        const estimatedCompletion = calculateProjectCompletionDate(tasks);
        const baseStatus = calculateRiskStatus(estimatedCompletion, committedDate);

        // Upgrade at_risk to overdue when the committed date has passed in calendar time
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const riskStatus: RiskStatus =
            baseStatus === 'at_risk' && committedDate && committedDate < today
                ? 'overdue'
                : baseStatus;

        const daysDifference =
            estimatedCompletion && committedDate
                ? calculateDaysDifference(estimatedCompletion, committedDate)
                : 0;

        return {
            estimatedCompletion,
            committedDate,
            riskStatus,
            daysDifference,
            isAtRisk: riskStatus === 'at_risk' || riskStatus === 'overdue',
        };
    }, [project, tasks]);
};
